import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
} from '../icons';
import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  Paper,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCart } from '../../contexts/CartContext';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import { getHoverColor, hexToRgba } from '../../utils/colorUtils';
import MenuItemDetailsDialog from '../menu/MenuItemDetailsDialog';

const EmptyCartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  height: '50vh',
  textAlign: 'center',
}));

const OptionsList = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

const CartSummary = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  padding: theme.spacing(3.5),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.15)',
}));

const CartDrawer = ({ open, onClose, onCheckout }) => {
  const {
    items,
    updateItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    orderBreakdown
  } = useCart();

  const { formatCurrency } = useCurrencyFormatter();
  const { primaryColor } = usePublicTheme();
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  const handleEditItem = (index) => {
    setEditingItemIndex(index);
  };

  const handleUpdateItem = (index, updatedFields) => {
    updateItem(index, updatedFields);
    setEditingItemIndex(null);
  };

  // Helper function to calculate item price including options
  const calculateItemPrice = (item) => {
    let itemPrice = parseFloat(item.price) || 0;

    if (item.options && item.options.length > 0) {
      item.options.forEach(option => {
        const optionPrice = parseFloat(option.price_addition) || 0;
        itemPrice += optionPrice;
      });
    }

    return itemPrice;
  };

  const handleUpdateQuantity = (index, change) => {
    const item = items[index];
    const newQuantity = Math.max(0, item.quantity + change);

    if (newQuantity === 0) {
      removeItem(index);
    } else {
      updateItemQuantity(index, newQuantity);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: { xs: 2.5, sm: 3.5 },
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
          color: 'white',
          boxShadow: `0 8px 30px ${hexToRgba(primaryColor, 0.35)}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <ShoppingCartIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.01em', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Your Order
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'rotate(90deg) scale(1.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {items.length === 0 ? (
        <EmptyCartContainer>
          <ShoppingCartIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Your cart is empty
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add items from the menu to get started
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              mt: 3,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              borderRadius: 4,
              px: { xs: 4, sm: 5 },
              py: { xs: 1.5, sm: 2 },
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              textTransform: 'none',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
              color: '#fff',
              boxShadow: `0 8px 25px ${hexToRgba(primaryColor, 0.35)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 35px ${hexToRgba(primaryColor, 0.45)}`,
              }
            }}
          >
            Browse Menu
          </Button>
        </EmptyCartContainer>
      ) : (
        <>
          <Box sx={{ flexGrow: 1, overflow: 'auto', pb: 8 }}>
            <List disablePadding>
              {items.map((item, index) => (
                <ListItem
                  key={`${item.id}-${index}`}
                  sx={{
                    px: 2,
                    py: 3,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: hexToRgba(primaryColor, 0.04),
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleEditItem(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          if (e.key === ' ') e.preventDefault();
                          handleEditItem(index);
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        p: 0.5,
                        mx: -0.5,
                        transition: 'background 0.2s ease, box-shadow 0.2s ease',
                        outline: 'none',
                        '&:hover': {
                          backgroundColor: hexToRgba(primaryColor, 0.06),
                        },
                        '&:focus-visible': {
                          boxShadow: `0 0 0 2px ${hexToRgba(primaryColor, 0.5)}`,
                        },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="subtitle1">
                            {item.name}
                          </Typography>
                          <EditIcon sx={{ fontSize: '0.85rem', color: '#9ca3af' }} />
                        </Box>
                        {item.options && item.options.length > 0 && (
                          <OptionsList>
                            {item.options.map((option, optIndex) => (
                              <Typography key={option.id} variant="body2" component="div">
                                • {option.name}
                                {option.price_addition > 0 &&
                                  ` (+${formatCurrency(option.price_addition)})`
                                }
                              </Typography>
                            ))}
                          </OptionsList>
                        )}
                        {item.notes && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, fontStyle: 'italic' }}
                          >
                            Note: {item.notes}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body1" fontWeight="bold" sx={{ flexShrink: 0 }}>
                        {formatCurrency(calculateItemPrice(item) * item.quantity)}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mt={1}
                    >
                      <Box display="flex" alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(index, -1)}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ mx: 1 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(index, 1)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => removeItem(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>

          <CartSummary elevation={0}>
            <Box mb={2}>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2">{formatCurrency(orderBreakdown.subtotal)}</Typography>
                </Grid>

                {orderBreakdown.discountDetails.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight="medium" color="text.secondary" sx={{ mt: 0.5 }}>
                        Discounts:
                      </Typography>
                    </Grid>
                    {orderBreakdown.discountDetails.map((d) => (
                      <React.Fragment key={d.id}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="success.main">
                            {d.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" color="success.main">
                            -{formatCurrency(d.amount)}
                          </Typography>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </>
                )}

                {orderBreakdown.tax > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Tax ({orderBreakdown.taxPercentage}%)</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">{formatCurrency(orderBreakdown.tax)}</Typography>
                    </Grid>
                  </>
                )}

                {orderBreakdown.serviceCharge > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Service Charge ({orderBreakdown.serviceChargePercentage}%)</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">{formatCurrency(orderBreakdown.serviceCharge)}</Typography>
                    </Grid>
                  </>
                )}

                {orderBreakdown.platformFee > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Platform Fee</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">{formatCurrency(orderBreakdown.platformFee)}</Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Total</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="subtitle1" fontWeight="bold">{formatCurrency(orderBreakdown.total)}</Typography>
                </Grid>
              </Grid>
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={onCheckout}
              startIcon={<ShoppingCartIcon sx={{ fontSize: 24 }} />}
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 4,
                py: 2.2,
                fontWeight: 800,
                fontSize: '1.15rem',
                textTransform: 'none',
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${getHoverColor(primaryColor, 'darken', 15)} 100%)`,
                color: '#fff',
                boxShadow: `0 10px 30px ${hexToRgba(primaryColor, 0.35)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${getHoverColor(primaryColor, 'darken', 10)} 0%, ${getHoverColor(primaryColor, 'darken', 20)} 100%)`,
                  transform: 'translateY(-4px)',
                  boxShadow: `0 15px 40px ${hexToRgba(primaryColor, 0.45)}`,
                }
              }}
            >
              Checkout - {formatCurrency(orderBreakdown.total)}
            </Button>

            <Button
              variant="text"
              fullWidth
              size="medium"
              onClick={clearCart}
              sx={{
                mt: 2,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 3,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                border: 'none',
                color: '#6b7280',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  color: '#ef4444',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              Clear Cart
            </Button>
          </CartSummary>
        </>
      )}
      {editingItemIndex !== null && items[editingItemIndex] && (
        <MenuItemDetailsDialog
          open={editingItemIndex !== null}
          onClose={() => setEditingItemIndex(null)}
          item={items[editingItemIndex]}
          onAddToCart={() => {}}
          editMode
          editIndex={editingItemIndex}
          onUpdate={handleUpdateItem}
          initialQuantity={items[editingItemIndex].quantity}
          initialOptions={items[editingItemIndex].options}
          initialNotes={items[editingItemIndex].notes}
        />
      )}
    </Drawer>
  );
};

export default CartDrawer;
